import React, { useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import {
  Box,
  Typography,
  Card,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Menu,
  MenuItem,
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material'
import {
  Refresh,
  MoreVert,
  ToggleOn,
  ToggleOff,
  Delete,
  Add,
  Save
} from '@mui/icons-material'
import * as actions from '../../actions.es6.js'
import AlertDialog from '../../../../components/AlertDialog/index.es6'
import RatingImportPanel from './RatingImportPanel'
import RatingSnapshotsPanel from './RatingSnapshotsPanel'

const navy = '#173E4B'
const NEW = '__new__'

const TABLE_TYPES = [
  { value: 'base_rate', label: 'Base Rate — cell replaces the base' },
  { value: 'adj_factor', label: 'Adjustment Factor — result × cell' },
  { value: 'adj_amount', label: 'Adjustment Amount — result + cell' },
  { value: 'adj_factor_quantity', label: 'Adjustment Factor (Quantity)' },
  { value: 'cap', label: 'Cap — result = min(result, cell)' }
]
const TABLE_TYPE_LABEL = {
  base_rate: 'Base Rate',
  adj_factor: 'Adjustment Factor',
  adj_amount: 'Adjustment Amount',
  adj_factor_quantity: 'Adjustment Factor (Qty)',
  cap: 'Cap'
}

const slug = s =>
  (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')

const uid = p => (p || 'id') + '-' + Math.random().toString(36).slice(2, 8)

function blankDraft() {
  return {
    guid: null,
    version: null,
    code: '',
    name: '',
    description: '',
    table_type: 'base_rate',
    active: true,
    dimensions: ['dependant.age'],
    segments: [],
    cells: []
  }
}

// Normalise a server table into an editable draft (rows get a client _rid key).
function toDraft(t) {
  return {
    guid: t.guid,
    version: t.version,
    code: t.code || '',
    name: t.name || '',
    description: t.description || '',
    table_type: t.table_type || 'base_rate',
    active: t.active !== false,
    dimensions: (t.dimensions || []).slice(),
    segments: (t.segments || []).map(s => ({ _rid: uid('seg'), label: s.label || '', expression: s.expression || '' })),
    cells: (t.cells || []).map(c => ({
      _rid: uid('cell'),
      segment_refs: (c.segment_refs || []).slice(),
      valueType: c.value_formula ? 'formula' : c.value_ref ? 'ref' : 'cents',
      value:
        c.value_formula != null && c.value_formula !== '' ? c.value_formula
          : c.value_ref != null && c.value_ref !== '' ? c.value_ref
            : c.value_in_cents != null ? String(c.value_in_cents) : ''
    }))
  }
}

function toPayload(d) {
  const code = (d.code && d.code.trim()) || slug(d.name) || uid('rt')
  return {
    code,
    name: (d.name && d.name.trim()) || code,
    description: (d.description && d.description.trim()) || undefined,
    table_type: d.table_type,
    dimensions: d.dimensions.map(x => (x || '').trim()).filter(Boolean),
    segments: d.segments
      .filter(s => s.label && s.label.trim())
      .map(s => ({ label: s.label.trim(), expression: (s.expression || '').trim() })),
    cells: d.cells
      .filter(c => (c.segment_refs || []).length)
      .map(c => {
        const cell = { segment_refs: c.segment_refs }
        if (c.valueType === 'formula') cell.value_formula = c.value
        else if (c.valueType === 'ref') cell.value_ref = c.value
        else cell.value_in_cents = c.value === '' ? 0 : Number(c.value)
        return cell
      })
  }
}

function RatingTablesScreen(props) {
  const { dispatch } = props
  const data = props.genericProductsData || {}
  // This screen has full create/edit/activate/delete CRUD, so it shows the
  // whole partner catalogue — not just tables this product's rating nodes
  // already reference. A table created here (or existing but not yet linked
  // to any node) must stay visible, or it becomes unreachable: nothing else
  // lets you browse the catalogue to attach it in the first place.
  const tables = data.ratingTables || []
  // Package codes for this product, memoised so RatingSnapshotsPanel's fetch
  // effect doesn't re-fire every render (the parent passes a fresh array).
  const currentPackages = (data.currentProduct && data.currentProduct.packages) || []
  const packageCodesKey = currentPackages.map(p => p.code).join(',')
  const packageCodes = useMemo(
    () => currentPackages.map(p => p.code).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageCodesKey]
  )
  const [selected, setSelected] = useState(null)
  const [draft, setDraft] = useState(null)
  const [menuEl, setMenuEl] = useState(null)
  // In-app confirm for deletes (replaces window.confirm). Holds the rating
  // table pending deletion, or null when the dialog is closed.
  const [pendingDelete, setPendingDelete] = useState(null)

  useEffect(() => {
    dispatch(actions.getRatingTablesRequest())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load the selected table into an editable draft. Keep a valid selection.
  useEffect(() => {
    if (selected === NEW) return
    if (tables.length && !tables.some(t => t.guid === selected)) {
      setSelected(tables[0].guid)
      return
    }
    const t = tables.find(x => x.guid === selected)
    if (t) setDraft(toDraft(t))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, tables])

  // After a successful save, refresh drops us back to the list view.
  useEffect(() => {
    if (data.ratingTableActionSuccess === 'saved' && selected === NEW) {
      setSelected(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.ratingTableActionSuccess])

  const startNew = () => {
    setSelected(NEW)
    setDraft(blankDraft())
  }

  const patch = p => setDraft(d => ({ ...d, ...p }))

  const doAction = (guid, action) => dispatch(actions.ratingTableActionRequest({ guid, action }))

  const save = () => {
    if (!draft) return
    dispatch(
      actions.saveRatingTableRequest({
        guid: draft.guid || undefined,
        version: draft.version,
        table: toPayload(draft)
      })
    )
  }

  // ── segment / cell / dimension editors ─────────────────────────────────────
  const setSeg = (rid, p) => patch({ segments: draft.segments.map(s => (s._rid === rid ? { ...s, ...p } : s)) })
  const addSeg = () => patch({ segments: draft.segments.concat({ _rid: uid('seg'), label: '', expression: '' }) })
  const rmSeg = rid => patch({ segments: draft.segments.filter(s => s._rid !== rid) })

  const setCell = (rid, p) => patch({ cells: draft.cells.map(c => (c._rid === rid ? { ...c, ...p } : c)) })
  const addCell = () => patch({ cells: draft.cells.concat({ _rid: uid('cell'), segment_refs: [], valueType: 'cents', value: '' }) })
  const rmCell = rid => patch({ cells: draft.cells.filter(c => c._rid !== rid) })

  const setDim = (i, v) => patch({ dimensions: draft.dimensions.map((d, idx) => (idx === i ? v : d)) })
  const addDim = () => patch({ dimensions: draft.dimensions.concat('') })
  const rmDim = i => patch({ dimensions: draft.dimensions.filter((_, idx) => idx !== i) })

  const feedbackOpen = Boolean(data.ratingTableActionSuccess || data.ratingTableActionError)
  const segLabels = draft ? draft.segments.map(s => s.label).filter(Boolean) : []

  return (
    <Box>
      {props.productGuid && <RatingImportPanel productGuid={props.productGuid} />}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', flex: 1 }}>
          Rating Tables
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            reusable rate matrices, chained onto rating nodes
          </Typography>
        </Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={startNew}
          sx={{ textTransform: 'none', bgcolor: navy, '&:hover': { bgcolor: '#0f2d38' } }}>
          New Table
        </Button>
        <Tooltip title="Refresh">
          <IconButton onClick={() => dispatch(actions.getRatingTablesRequest())} sx={{ color: navy }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {data.ratingTablesError && (
        <Alert severity="error" sx={{ mb: 2 }}>{data.ratingTablesError.message || data.ratingTablesError}</Alert>
      )}

      {data.ratingTablesLoader ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: navy }} /></Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Master list */}
          <Card variant="outlined" sx={{ width: 280, maxWidth: '100%', maxHeight: 620, overflow: 'auto' }}>
            {tables.length === 0 && (
              <Box sx={{ p: 3, color: '#9CA3AF', fontSize: 13 }}>No rating tables yet.</Box>
            )}
            {tables.map(t => {
              const isSel = t.guid === selected
              return (
                <Box key={t.guid} onClick={() => setSelected(t.guid)}
                  sx={{
                    px: 2, py: 1.25, cursor: 'pointer',
                    borderLeft: '3px solid ' + (isSel ? navy : 'transparent'),
                    bgcolor: isSel ? 'rgba(23,62,75,0.05)' : 'transparent',
                    borderBottom: '1px solid #F3F4F6',
                    '&:hover': { bgcolor: isSel ? 'rgba(23,62,75,0.05)' : '#FAFAFA' }
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, flex: 1 }} noWrap>{t.name || t.code}</Typography>
                    {t.active === false && <Chip label="inactive" size="small" sx={{ height: 18, fontSize: 9 }} />}
                  </Box>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    {(TABLE_TYPE_LABEL[t.table_type] || t.table_type)} · {(t.segments || []).length} seg · {(t.cells || []).length} cells
                  </Typography>
                </Box>
              )
            })}
          </Card>

          {/* Editor */}
          <Card variant="outlined" sx={{ flex: 1, minWidth: 340, p: 2.5 }}>
            {!draft ? (
              <Typography color="text.secondary">Select a rating table, or create a new one.</Typography>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937', flex: 1 }}>
                    {selected === NEW ? 'New Rating Table' : 'Edit Rating Table'}
                  </Typography>
                  {selected !== NEW && (
                    <>
                      <Chip label={draft.active ? 'Active' : 'Inactive'} size="small"
                        sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: draft.active ? '#F0FDF4' : '#FEF2F2', color: draft.active ? '#15803D' : '#B91C1C' }} />
                      <IconButton size="small" onClick={e => setMenuEl(e.currentTarget)}><MoreVert /></IconButton>
                      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
                        <MenuItem onClick={() => { setMenuEl(null); doAction(draft.guid, draft.active ? 'deactivate' : 'activate') }}>
                          {draft.active ? <ToggleOff fontSize="small" sx={{ mr: 1, color: '#B45309' }} /> : <ToggleOn fontSize="small" sx={{ mr: 1, color: '#15803D' }} />}
                          {draft.active ? 'Deactivate' : 'Activate'}
                        </MenuItem>
                        <MenuItem sx={{ color: '#c62828' }}
                          onClick={() => { setMenuEl(null); setPendingDelete(draft) }}>
                          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField label="Name" size="small" value={draft.name} onChange={e => patch({ name: e.target.value })} />
                  <TextField label="Code" size="small" value={draft.code} onChange={e => patch({ code: e.target.value })}
                    placeholder={slug(draft.name) || 'auto'} helperText={selected === NEW ? 'Blank = auto from name' : ' '} />
                  <FormControl size="small">
                    <InputLabel>Table Type</InputLabel>
                    <Select label="Table Type" value={draft.table_type} onChange={e => patch({ table_type: e.target.value })}>
                      {TABLE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label="Description" size="small" value={draft.description} onChange={e => patch({ description: e.target.value })} />
                </Box>

                {/* Dimensions */}
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7280', mb: 1 }}>Dimensions (fact refs)</Typography>
                {draft.dimensions.map((d, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField size="small" value={d} onChange={e => setDim(i, e.target.value)} placeholder="dependant.age" sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => rmDim(i)} sx={{ color: '#c62828' }}><Delete fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />} onClick={addDim} sx={{ textTransform: 'none', color: navy, mb: 2 }}>Add dimension</Button>

                <Divider sx={{ mb: 2 }} />

                {/* Segments */}
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7280', mb: 1 }}>Segments</Typography>
                {draft.segments.map(s => (
                  <Box key={s._rid} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField size="small" label="Label" value={s.label} onChange={e => setSeg(s._rid, { label: e.target.value })} sx={{ width: 160 }} />
                    <TextField size="small" label="Expression" value={s.expression} onChange={e => setSeg(s._rid, { expression: e.target.value })}
                      placeholder="dependant.age >= 6 and dependant.age <= 13" sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => rmSeg(s._rid)} sx={{ color: '#c62828' }}><Delete fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />} onClick={addSeg} sx={{ textTransform: 'none', color: navy, mb: 2 }}>Add segment</Button>

                <Divider sx={{ mb: 2 }} />

                {/* Cells */}
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7280', mb: 1 }}>
                  Cells <Typography component="span" variant="caption" color="text.secondary">— match segments → value; first matching cell wins</Typography>
                </Typography>
                {draft.cells.map(c => (
                  <Box key={c._rid} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                      <InputLabel>Segments</InputLabel>
                      <Select multiple value={c.segment_refs} input={<OutlinedInput label="Segments" />}
                        onChange={e => setCell(c._rid, { segment_refs: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                        renderValue={sel => sel.join(', ')}>
                        {segLabels.length === 0 && <MenuItem disabled value="">Add segments first</MenuItem>}
                        {segLabels.map(l => (
                          <MenuItem key={l} value={l}>
                            <Checkbox checked={c.segment_refs.indexOf(l) > -1} size="small" />
                            <ListItemText primary={l} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ width: 130 }}>
                      <InputLabel>Value type</InputLabel>
                      <Select label="Value type" value={c.valueType} onChange={e => setCell(c._rid, { valueType: e.target.value })}>
                        <MenuItem value="cents">Amount (cents)</MenuItem>
                        <MenuItem value="ref">Value ref</MenuItem>
                        <MenuItem value="formula">Formula</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Value" value={c.value} onChange={e => setCell(c._rid, { value: e.target.value })}
                      type={c.valueType === 'cents' ? 'number' : 'text'} sx={{ width: 160 }} />
                    <IconButton size="small" onClick={() => rmCell(c._rid)} sx={{ color: '#c62828' }}><Delete fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />} onClick={addCell} sx={{ textTransform: 'none', color: navy, mb: 2 }}>Add cell</Button>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  {selected === NEW && (
                    <Button onClick={() => { setSelected(tables[0] ? tables[0].guid : null) }} sx={{ textTransform: 'none' }}>Cancel</Button>
                  )}
                  <Button variant="contained" startIcon={<Save />} disabled={data.ratingTableSaveLoader || !draft.name.trim()}
                    onClick={save} sx={{ textTransform: 'none', bgcolor: navy, '&:hover': { bgcolor: '#0f2d38' } }}>
                    {data.ratingTableSaveLoader ? 'Saving…' : selected === NEW ? 'Create Table' : 'Save Changes'}
                  </Button>
                </Box>
              </Box>
            )}
          </Card>
        </Box>
      )}

      {props.productGuid && (
        <Box sx={{ mt: 3 }}>
          <RatingSnapshotsPanel productGuid={props.productGuid} packageCodes={packageCodes} />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Creating a new table applies immediately. Editing, deactivating, or
        deleting an existing one may require a second user's approval, opening a
        change request in the Rating queue instead of applying right away.
      </Typography>

      <Snackbar open={feedbackOpen} autoHideDuration={5000}
        onClose={() => dispatch(actions.resetRatingTableFeedback())}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {feedbackOpen ? (
          <Alert severity={data.ratingTableActionError ? 'error' : 'success'} variant="filled"
            onClose={() => dispatch(actions.resetRatingTableFeedback())} sx={{ width: '100%' }}>
            {data.ratingTableActionError
              ? (data.ratingTableActionError.message || data.ratingTableActionError)
              : data.ratingTableActionSuccess === 'save-pending'
                ? 'Rating table change request submitted' +
                  (data.ratingTableSaveChangeRequestGuid
                    ? ' (' + data.ratingTableSaveChangeRequestGuid + ')'
                    : '') +
                  ' — pending approval.'
                : data.ratingTableActionSuccess === 'saved'
                  ? 'Rating table saved.'
                  : 'Rating table ' + data.ratingTableActionSuccess + ' request submitted.'}
          </Alert>
        ) : undefined}
      </Snackbar>

      <AlertDialog
        warning
        show={!!pendingDelete}
        size="sm"
        title={pendingDelete ? 'Delete rating table "' + (pendingDelete.name || pendingDelete.code) + '"?' : ''}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => setPendingDelete(null)}
        onCancel={() => {
          const target = pendingDelete
          setPendingDelete(null)
          if (target) { doAction(target.guid, 'delete'); setSelected(null) }
        }}
        confirmBtnText={'Cancel'}
        showCancel={true}
        showConfirm={true}
        cancelBtnText={'Delete'}
        confirmBtnCssClass
      />
    </Box>
  )
}

export default connect(state => ({
  genericProductsData: state.genericProducts
}))(RatingTablesScreen)
